import type { ReactNode } from "react";
import { cssVars } from "../lib/css";

/**
 * Iconos del sistema. Van en un grid de 16×16 renderizados a 1rem, así que
 * cada unidad del viewBox equivale a 1 píxel al tamaño por defecto: los trazos se
 * declaran en CSS (`stroke-width: 0.125rem`) y escalan con el texto.
 *
 * Nunca son decorativos: acompañan a una etiqueta. Un color de estado sin
 * icono y sin texto no comunica nada a quien no distingue el color.
 */

export type IconName =
  | "alert"
  | "arrow-down"
  | "arrow-right"
  | "arrow-up"
  | "book"
  | "calculator"
  | "chart"
  | "check"
  | "check-circle"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right"
  | "clock"
  | "compass"
  | "external"
  | "flag"
  | "info"
  | "layers"
  | "lightbulb"
  | "list"
  | "minus"
  | "play"
  | "refresh"
  | "scale"
  | "search"
  | "sigma"
  | "sparkle"
  | "stop"
  | "table"
  | "target"
  | "x";

const GLYPHS: Record<IconName, ReactNode> = {
  alert: (
    <>
      <path d="M8 2.3 14.4 13.2H1.6L8 2.3Z" />
      <path d="M8 6.4v3.1" />
      <path d="M8 11.6h.01" />
    </>
  ),
  "arrow-down": (
    <>
      <path d="M8 2.8v10.4" />
      <path d="M4.2 9.4 8 13.2l3.8-3.8" />
    </>
  ),
  "arrow-right": (
    <>
      <path d="M2.8 8h10.4" />
      <path d="M9.4 4.2 13.2 8l-3.8 3.8" />
    </>
  ),
  "arrow-up": (
    <>
      <path d="M8 13.2V2.8" />
      <path d="M4.2 6.6 8 2.8l3.8 3.8" />
    </>
  ),
  book: (
    <>
      <path d="M8 4.3A2.3 2.3 0 0 0 5.7 2H2v11h3.7A2.3 2.3 0 0 1 8 15.3" />
      <path d="M8 4.3A2.3 2.3 0 0 1 10.3 2H14v11h-3.7A2.3 2.3 0 0 0 8 15.3" />
    </>
  ),
  calculator: (
    <>
      <rect x="2.8" y="1.8" width="10.4" height="12.4" rx="1.4" />
      <path d="M5.6 4.6h4.8" />
      <path d="M5.6 8h.01M8 8h.01M10.4 8h.01M5.6 10.9h.01M8 10.9h.01M10.4 10.9h.01" />
    </>
  ),
  chart: (
    <>
      <path d="M2.4 13.4h11.2" />
      <path d="M4.4 13.4V8.6M7.2 13.4V4.4M10 13.4V7M12.8 13.4V2.6" />
    </>
  ),
  check: <path d="M3.2 8.4 6.4 11.6 12.8 4.8" />,
  "check-circle": (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <path d="M5.4 8.2 7.3 10.1l3.3-3.8" />
    </>
  ),
  "chevron-down": <path d="M4 6.3 8 10.3l4-4" />,
  "chevron-left": <path d="M9.8 3.6 5.4 8l4.4 4.4" />,
  "chevron-right": <path d="M6.2 3.6 10.6 8l-4.4 4.4" />,
  clock: (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <path d="M8 4.6V8l2.4 1.6" />
    </>
  ),
  compass: (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <path d="M10.4 5.6 9.1 9.1 5.6 10.4 6.9 6.9Z" />
    </>
  ),
  external: (
    <>
      <path d="M9.4 2.8h3.8v3.8" />
      <path d="M13.2 2.8 7.8 8.2" />
      <path d="M11.4 9.6v3.2a1.2 1.2 0 0 1-1.2 1.2H3.8a1.2 1.2 0 0 1-1.2-1.2V6.4a1.2 1.2 0 0 1 1.2-1.2h3.2" />
    </>
  ),
  flag: (
    <>
      <path d="M3.8 13.6V2.4" />
      <path d="M3.8 3.2h7.9l-1.6 2.6 1.6 2.6H3.8" />
    </>
  ),
  info: (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <path d="M8 7.2v4" />
      <path d="M8 4.9h.01" />
    </>
  ),
  layers: (
    <>
      <path d="M8 2.2 14.2 5.4 8 8.6 1.8 5.4Z" />
      <path d="m1.8 8.6 6.2 3.2 6.2-3.2" />
      <path d="m1.8 11.4 6.2 3.2 6.2-3.2" />
    </>
  ),
  lightbulb: (
    <>
      <path d="M8 2.2a3.6 3.6 0 0 1 2.3 6.4v1.7H5.7V8.6A3.6 3.6 0 0 1 8 2.2Z" />
      <path d="M6.4 12.6h3.2" />
      <path d="M7 14.2h2" />
    </>
  ),
  list: (
    <>
      <path d="M5.8 4.2h7.4M5.8 8h7.4M5.8 11.8h7.4" />
      <path d="M2.8 4.2h.01M2.8 8h.01M2.8 11.8h.01" />
    </>
  ),
  minus: <path d="M3.6 8h8.8" />,
  play: <path d="M5.4 3.2 12.4 8l-7 4.8Z" />,
  refresh: (
    <>
      <path d="M13.2 8a5.2 5.2 0 1 1-1.7-3.9" />
      <path d="M13.2 2.4v3.2H10" />
    </>
  ),
  scale: (
    <>
      <path d="M8 2.4v11.2" />
      <path d="M3.4 13.6h9.2" />
      <path d="M2.2 5.6h11.6" />
      <path d="M2.2 5.6 4.4 10h-4.4Z" />
      <path d="M13.8 5.6 11.6 10h4.4Z" />
    </>
  ),
  search: (
    <>
      <circle cx="7.2" cy="7.2" r="4.4" />
      <path d="m10.6 10.6 2.8 2.8" />
    </>
  ),
  sigma: <path d="M12.2 3H4.6L8.6 8l-4 5h7.6" />,
  sparkle: (
    <>
      <path d="M6.4 1.8 7.4 4.8l3 1-3 1-1 3-1-3-3-1 3-1Z" />
      <path d="M12 8.4l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7Z" />
    </>
  ),
  stop: <rect x="4.2" y="4.2" width="7.6" height="7.6" rx="1" />,
  table: (
    <>
      <rect x="2.2" y="3" width="11.6" height="10" rx="1.2" />
      <path d="M2.2 6.4h11.6M6.6 6.4V13" />
    </>
  ),
  target: (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <circle cx="8" cy="8" r="3.1" />
      <circle cx="8" cy="8" r="0.6" />
    </>
  ),
  x: <path d="M4.2 4.2 11.8 11.8M11.8 4.2 4.2 11.8" />,
};

const FILLED: ReadonlySet<IconName> = new Set<IconName>(["play", "sparkle"]);

export interface IconProps {
  name: IconName;
  /** Longitud CSS en `rem`; por defecto hereda el tamaño del texto. */
  size?: string;
  className?: string;
  /** Si se pasa, el icono deja de ser decorativo y se anuncia. */
  title?: string;
}

export function Icon({ name, size, className, title }: IconProps) {
  const classes = ["icon", FILLED.has(name) ? "icon--filled" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <svg
      className={classes}
      viewBox="0 0 16 16"
      style={size ? cssVars({ "--icon-size": size }) : undefined}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {GLYPHS[name]}
    </svg>
  );
}
